import EInvoicingConfiguration from "./config.type";
import { Configuration as DevConfiguration } from './einvoicing-configuration.DEV';
import { Configuration as IntConfiguration } from './einvoicing-configuration.INT';
import { Configuration as AircapiConfiguration } from './einvoicing-configuration.AIRCAPI';
import { Configuration as ProdConfiguration } from './einvoicing-configuration.PROD';

// Vite injects `__VITE_REACT_APP_EINVOICING_ENVIRONMENT__` via `define` in
// `vite.config.ts`, performing a literal build-time substitution. Under CRA
// (webpack + babel-jest) the identifier doesn't exist, so the `try` throws
// ReferenceError and we fall back to `process.env.REACT_APP_*`. Using a
// custom identifier (not `import.meta.*`) keeps the source parsable by both
// build systems during the bake window -- Jest 27 ships with a babel config
// that does NOT accept the `import.meta` token. (assumptions.md v2
// Critique 5, risk #1.)
declare const __VITE_REACT_APP_EINVOICING_ENVIRONMENT__: string | undefined;

function readDeploymentEnvironment(): string | undefined {
    let viteValue: string | undefined;
    try {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        viteValue = __VITE_REACT_APP_EINVOICING_ENVIRONMENT__;
    } catch {
        viteValue = undefined;
    }
    if (viteValue !== undefined && viteValue !== '') {
        return viteValue;
    }
    const craValue =
        typeof process !== 'undefined' && process.env
            ? process.env.REACT_APP_EINVOICING_ENVIRONMENT
            : undefined;
    return craValue !== undefined && craValue !== '' ? craValue : undefined;
}

const deploymentEnvironment = readDeploymentEnvironment();

function selectConfiguration(env: string | undefined): EInvoicingConfiguration {
    switch (env) {
        case 'INT':
            return IntConfiguration;
        case 'AIRCAPI':
            return AircapiConfiguration;
        case 'PROD':
            return ProdConfiguration;
        case undefined:
        case 'DEV':
            return DevConfiguration;
        default:
            throw new Error(
                `[einvoicing-configuration] Unknown REACT_APP_EINVOICING_ENVIRONMENT '${env}'. ` +
                    `Expected one of: INT, AIRCAPI, PROD, DEV (or unset for DEV).`
            );
    }
}

const Configuration: EInvoicingConfiguration = selectConfiguration(deploymentEnvironment);

// Always send the MSAL auth redirect back to the origin actually serving the
// app instead of the hardcoded per-environment redirectUri. Every deployed
// origin (localhost:3000, INT, PROD, AIRCAPI) is a registered redirect URI on
// the shared AAD app, so window.location.origin always matches a registered
// value. This makes auth resilient to a mis-resolved deployment environment:
// if REACT_APP_EINVOICING_ENVIRONMENT fails to inject at build time (e.g. the
// Vite `define` bakes an empty string) we fall back to DevConfiguration, whose
// redirectUri is http://localhost:3000 -- which then bounces the AAD response
// to localhost and breaks sign-in on real hosts (INT/PROD). Deriving the
// redirect from the live origin removes that failure mode entirely. Guarded for
// non-browser contexts (SSR / tests without a DOM) so the static per-env
// redirectUri stays the fallback there.
if (typeof window !== 'undefined' && window.location?.origin) {
    Configuration.msalConfig.auth.redirectUri = window.location.origin;
}

// Runtime guard against env-var fall-through (assumptions.md v2 Critique 5,
// risk #1). If we picked the deployment config but MSAL is missing a clientId,
// auth would die on the first call -- fail loudly at boot instead.
if (deploymentEnvironment === 'INT' || deploymentEnvironment === 'AIRCAPI' || deploymentEnvironment === 'PROD') {
    const clientId = Configuration.msalConfig?.auth?.clientId;
    if (!clientId) {
        throw new Error(
            `[einvoicing-configuration] msalConfig.auth.clientId is missing for environment '${deploymentEnvironment}'.`
        );
    }
}

export { readDeploymentEnvironment, selectConfiguration };
export default Configuration;